import React, { useState, useEffect } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import isEmpty from 'lodash/isEmpty';

import { fetchFantasyVerse } from 'Redux/ducks/algolia-calls';
import useViewport from 'Hooks/useViewport';
import imgixConfig from 'Config/imgix';
import Loading from 'Components/Utility/Loading';
import Meta from 'Components/Utility/SEO/Meta';
import NotFound from 'Components/NotFound/NotFound';
import TakeOver from 'Components/Banner/TakeOver';
import ItemList from 'Components/Utility/SEO/ItemList';
import HitCard from 'Components/Cards/HitCard';
import PublisherCarousel from 'Components/Carousel/PublisherCarousel/PublisherCarousel';
import FeaturedCarouselContainer from 'Components/Carousel/FeaturedCarouselContainer';
import MidPageBannerContainer from 'Components/Banner/MidPageBanner/MidPageBannerContainer';
import EmailSignupPlacement from 'Components/Banner/EmailSignupPlacement/EmailSignupPlacement';
import FantasyVerseFreeComics from 'Features/FantasyVerse/components/FantasyVerseFreeComics';
import TrackedLink from 'Components/TrackedLink/TrackedLink';

import 'Features/FantasyVerse/pages/FantasyVersePage.scss';

const messages = defineMessages({
  title: {
    id: 'fantasyverse-page.seotitle',
    defaultMessage: 'FantasyVerse | Digital comics, Graphic novels and Tabletop games | Fanatical',
  },
  desc: {
    id: 'fantasyverse-page.seodesc',
    defaultMessage: 'Embark on a fantasy adventure with FantasyVerse, your gateway to all things fantasy—from anime and manga to sci-fi, horror and RPGs. Dive deeper into the worlds you love with our curated selection of digital comics, graphic novels, tabletop games, and more.',
  },
  fantasyverseBundles: {
    id: 'fantasyverse-page.section.heading.fantasyverseBundles',
    defaultMessage: 'FantasyVerse Bundles',
  },
  audio: {
    id: 'fantasyverse-page.section.heading.audio',
    defaultMessage: 'Audiobooks',
  },
});

export default function FantasyVersePage() {
  const [algoliaData, setAlgoliaData] = useState(null);

  const { formatMessage } = useIntl();

  const { isAboveLg } = useViewport();

  useEffect(() => {
    const fetchData = async () => {
      const data = await fetchFantasyVerse();

      setAlgoliaData(data);
    };

    fetchData();
  }, []);

  // null is the default state and implies component hasn't loaded
  if (algoliaData === null) return <Loading />;

  if (isEmpty(algoliaData)) return <NotFound />;

  const { topSellers } = algoliaData;

  const tempTopSellers = topSellers;

  return (
    <React.Fragment>
      <Meta
        title={formatMessage(messages.title)}
        description={formatMessage(messages.desc)}
        url="fantasyverse"
        pageType="CollectionPage"
        hits={tempTopSellers}
      />

      <TakeOver appearsOn="fantasyverse" />

      <main className="FantasyVersePage">
        <section className="container">
          <FeaturedCarouselContainer fantasyVerse />
        </section>

        <EmailSignupPlacement needsContainer appearsOn="fantasyVerse" />

        <FantasyVerseFreeComics />

        <section className="container">
          <div className="section-heading-container">
            <h2 className="section-title">
              <FormattedMessage
                id="fantasyverse.section.heading.top-sellers"
                defaultMessage="Top Sellers"
              />
            </h2>
          </div>
          <ItemList hits={tempTopSellers} title="Top Sellers" />

          <ul className="HitCardsRow HitCardsRow--4x5 HitCardsRow--marginReset row">
            {tempTopSellers.slice(0, 20).map(hit =>
              <li className="HitCardsRow__column" key={hit.objectID}>
                <HitCard
                  adaptiveSize
                  hit={hit}
                  xsSize={12}
                  smSize={12}
                  mdSize={12}
                  lgSize={12}
                  hitCardNameTag="h3"
                  coverTypeOverride="portrait"
                />
              </li>,
            )}
          </ul>
        </section>

        <section className="FantasyVersePage__welcome container">
          <div className="FantasyVersePage__welcome__content">
            <div className="FantasyVersePage__welcome__text">
              <h1 className="FantasyVersePage__welcome__text__heading">
                <FormattedMessage
                  id="fantasyverse.welcome.heading"
                  defaultMessage="Your portal to {span}"
                  values={{
                    span: <span><FormattedMessage id="fantasyverse.welcome.heading.span" defaultMessage="Fantasy Worlds" /></span>,
                  }}
                />
              </h1>
              <p className="FantasyVersePage__welcome__text__p">
                <FormattedMessage
                  id="fantasyverse.welcome.p.1"
                  defaultMessage="Step into the ultimate destination for fantasy, manga, sci-fi, horror, and RPG fans. Explore exclusive bundles, flash deals, and limited-time offers on a curated selection of digital comics, graphic novels, tabletop games, and more."
                />
              </p>
            </div>

            {isAboveLg &&
              <div className="FantasyVersePage__welcome__background">
                <img
                  srcSet={`
                https://fanatical.imgix.net/production/fantasyverse/fantasyverse-awakens-welcome-background.jpg?${imgixConfig}&w=2000&h=322&fit=crop 2000w,
                https://fanatical.imgix.net/production/fantasyverse/fantasyverse-awakens-welcome-background.jpg?${imgixConfig}&w=1750&h=282&fit=crop 1750w,
                https://fanatical.imgix.net/production/fantasyverse/fantasyverse-awakens-welcome-background.jpg?${imgixConfig}&w=1500&h=242&fit=crop 1500w,
                https://fanatical.imgix.net/production/fantasyverse/fantasyverse-awakens-welcome-background.jpg?${imgixConfig}&w=1324&h=213&fit=crop 1324w,
                https://fanatical.imgix.net/production/fantasyverse/fantasyverse-awakens-welcome-background.jpg?${imgixConfig}&w=1000&h=161&fit=crop 1000w,
                https://fanatical.imgix.net/production/fantasyverse/fantasyverse-awakens-welcome-background.jpg?${imgixConfig}&w=750&h=121&fit=crop 750w
              `}
                  sizes="100vw"
                  src={`https://fanatical.imgix.net/production/fantasyverse/fantasyverse-awakens-welcome-background.jpg?${imgixConfig}&w=2000&h=322&fit=crop`}
                  alt="FantasyVerse Background"
                />
              </div>
            }
          </div>
        </section>

        <PublisherCarousel type="fantasyverse" />

        <MidPageBannerContainer appearsOn="fantasyverse" />

        <section className="quick-links container">
          <h2 className="mb-3">
            <FormattedMessage
              id="fantasyverse.section.heading.browse"
              defaultMessage="Browse the FantasyVerse"
            />
          </h2>
          <div className="quicklinks-container">
            <div className="quick-link">
              <TrackedLink to="/search?types=book&collections=FantasyVerse" type="frontend/click/CLICK_INTERACTION" section="BrowseFantasyVerse">
                eBooks
              </TrackedLink>
            </div>
            <div className="quick-link">
              <TrackedLink to="/search?types=comic&collections=FantasyVerse" type="frontend/click/CLICK_INTERACTION" section="BrowseFantasyVerse">
                Comics
              </TrackedLink>
            </div>
            <div className="quick-link">
              <TrackedLink to="/search?types=graphic-novel&collections=FantasyVerse" type="frontend/click/CLICK_INTERACTION" section="BrowseFantasyVerse">
                Graphic Novels
              </TrackedLink>
            </div>
            <div className="quick-link">
              <TrackedLink to="/search?types=audio&collections=FantasyVerse" type="frontend/click/CLICK_INTERACTION" section="BrowseFantasyVerse">
                Audiobooks
              </TrackedLink>
            </div>
          </div>
        </section>
      </main>
    </React.Fragment>
  );
}
